import { useEffect, useRef, useState } from 'react';
import CreateInvoiceHeader from './createInvoiceHeader';
import { getCurrentDate } from '../../../../../utils/dateUtils';
import InvoiceForm from './invoiceForm';
import Button from '../../../../../component/ui/Button';
import useOfflineInvoice from '../components/useOfflineSaveInvoice';
import { fetchInvoiceNumber } from '../../../../../services/invoices';
import { useParams } from 'react-router-dom';

const CreateInvoice = () => {
	const param = useParams();
	const id = param?.id;
	const { saveInvoice, getInvoice, isOnline, deleteInvoice } = useOfflineInvoice();
	const [error, setError] = useState({});
	const [tableError, setTableError] = useState([]);
	const [isDraft, setIsDraft] = useState(false);
	const [showRestoreBanner, setShowRestoreBanner] = useState(false);
	const wentOfflineRef = useRef(false);
	const hasUserInteractedRef = useRef(false);
	const [additionDiscountAndTax, setAdditionDiscountAndTax] = useState({
		discount: null,
		adj: null,
		round_off: null,
	});
	const [invoice, setInvoiceState] = useState({
		customer_id: null,
		doctor_id: null,
		invoice_number: '',
		date: null,
		sale_order_id: null,
		remarks: '',
		payment_method: null,
		due_date: null,
	});
	const [invoiceTable, setInvoiceTableState] = useState([]);

	const setInvoice = (data) => {
		hasUserInteractedRef.current = true;
		setInvoiceState(data);
	};

	const setInvoiceTable = (data) => {
		hasUserInteractedRef.current = true;
		setInvoiceTableState(data);
	};

	function totalDiscountAmount() {
		let amount = invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);
		return (amount * Number(additionDiscountAndTax.discount ?? 0)) / 100;
	}

	function totalTaxesAmount() {
		let totalTax1 = invoiceTable?.reduce(
			(sum, item) =>
				Number(sum) + (Number(item.amount ?? 0) * Number(item.product_tax1 ?? 0)) / 100,
			0,
		);
		let totalTax2 = invoiceTable?.reduce(
			(sum, item) =>
				Number(sum) + (Number(item.amount ?? 0) * Number(item.product_tax2 ?? 0)) / 100,
			0,
		);
		return Number(totalTax1) + Number(totalTax2);
	}

	function totalAmount() {
		let amount = invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);
		let totalTaxes = totalTaxesAmount();

		let totalAmountWithDiscount =
			amount - (amount * Number(additionDiscountAndTax.discount ?? 0)) / 100;

		let totalAmountWithTax = Number(totalAmountWithDiscount) + Number(totalTaxes);

		return (
			Number(totalAmountWithTax) -
			Number(additionDiscountAndTax.adj ?? 0) -
			Number(additionDiscountAndTax.round_off ?? 0)
		);
	}

	const calculateAmount = () =>
		invoiceTable?.reduce((sum, item) => Number(sum) + Number(item.amount), 0);

	useEffect(() => {
		const saved = getInvoice();
		if (saved?.customer_id || saved?.invoiceTable?.length > 0) {
			setShowRestoreBanner(true);
		}
	}, []);

	useEffect(() => {
		if (!id) {
			if (isOnline) {
				if (wentOfflineRef.current) {
					wentOfflineRef.current = false;
					const saved = getInvoice();
					if (saved?.customer_id || saved?.invoiceTable?.length > 0) {
						setShowRestoreBanner(true);
					}
				}
				return;
			}
			wentOfflineRef.current = true;
			if (!hasUserInteractedRef.current) return;
			if (
				!isOnline &&
				invoice.invoice_number &&
				invoice.date &&
				(invoice.payment_method || invoice.customer_id || invoice.doctor_id || invoice.due_date)
			)
				setInvoiceOfflineData();
		}
	}, [isOnline, invoice, invoiceTable, additionDiscountAndTax]);

	const setInvoiceOfflineData = () => {
		const timeout = setTimeout(() => {
			saveInvoice({
				...invoice,
				invoiceTable,
				additionDiscountAndTax,
				updatedAt: Date.now(),
			});
		}, 500);

		return () => clearTimeout(timeout);
	};

	async function handleFetchInvoiceNumber() {
		try {
			const invoice = await fetchInvoiceNumber();
			setInvoiceState((prev) => ({
				...prev,
				invoice_number: invoice?.number,
				date: getCurrentDate(),
			}));
		} catch (error) {}
	}

	useEffect(() => {
		if (
			(invoice.payment_method ||
				invoice.customer_id ||
				invoice.doctor_id ||
				invoice.due_date ||
				(invoiceTable.length > 0 &&
					(invoiceTable[0].product_id !== '' ||
						invoiceTable[0].batch_no !== '' ||
						invoiceTable[0].quantity !== ''))) &&
			!id
		) {
			setInvoiceOfflineData();
		}
	}, [
		invoice.payment_method,
		invoice.customer_id,
		invoice.doctor_id,
		invoice.due_date,
		invoice.remarks,
		invoiceTable,
		additionDiscountAndTax,
	]);

	return (
		<div>
			<CreateInvoiceHeader
				totalDiscountAmount={totalDiscountAmount}
				totalTaxesAmount={totalTaxesAmount}
				totalAmount={totalAmount}
				calculateAmount={calculateAmount}
				additionDiscountAndTax={additionDiscountAndTax}
				error={error}
				setError={setError}
				tableError={tableError}
				setTableError={setTableError}
				invoice={invoice}
				setInvoice={setInvoice}
				invoiceTable={invoiceTable}
				setInvoiceTable={setInvoiceTable}
				setIsSetOfflineInvoice={setShowRestoreBanner}
				saveInvoice={saveInvoice}
				clearAllInvoices={deleteInvoice}
				getInvoice={getInvoice}
				isSetOfflineInvoice={showRestoreBanner}
				isDraft={isDraft}
			/>

			{/* ---------- Restore Banner ---------- */}
			{showRestoreBanner && (
				<div
					style={{
						backgroundColor: '#FFC008',
						padding: '5px 20px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<div style={{ fontWeight: 600 }}>Offline data found. Do you want to restore it?</div>

					<div className="d-flex gap-3">
						<Button
							className="mx-1 btn-gray"
							button="Clear"
							onClick={() => {
								deleteInvoice();
								handleFetchInvoiceNumber();
								setShowRestoreBanner(false);
							}}
						/>

						<Button
							className="btn-primary"
							button="Restore"
							onClick={() => {
								const data = getInvoice();
								if (!data) return;
								setInvoice(data);
								setInvoiceTable(data.invoiceTable);
								setAdditionDiscountAndTax(data.additionDiscountAndTax);
								deleteInvoice();
								setShowRestoreBanner(false);
							}}
						/>
					</div>
				</div>
			)}

			<InvoiceForm
				id={id}
				setInvoice={setInvoice}
				invoice={invoice}
				error={error}
				setError={setError}
				setIsDraft={setIsDraft}
				isDraft={isDraft}
				setInvoiceTable={setInvoiceTable}
				setAdditionDiscountAndTax={setAdditionDiscountAndTax}
				invoiceTable={invoiceTable}
				tableError={tableError}
				setTableError={setTableError}
				additionDiscountAndTax={additionDiscountAndTax}
				totalDiscountAmount={totalDiscountAmount}
				totalTaxesAmount={totalTaxesAmount}
				totalAmount={totalAmount}
				calculateAmount={calculateAmount}
				getInvoice={getInvoice}
				setInvoiceOfflineData={setInvoiceOfflineData}
			/>
		</div>
	);
};

export default CreateInvoice;
